<script lang="ts">
  import { NewPasswordSchema, PasswordInputSchema, normalizePassword } from '@clinic/contracts';
  import { useQueryClient } from '@tanstack/svelte-query';
  import { z } from 'zod';
  import { createMeQuery, meQueryKey } from '../../../shared/auth/auth.svelte';
  import { fa } from '../../../shared/i18n/fa';
  import { createForm } from '../../../shared/lib/form.svelte';
  import { online } from '../../../shared/lib/online.svelte';
  import { Alert, Button, ErrorAlert, Field, PasswordInput } from '../../../shared/ui';
  import { createChangePassword } from '../api/auth-api';

  /**
   * The signed-in person's own password (ADR-033 §9): the current one first, when there is one.
   * The other sessions of this person are closed by the server.
   */
  const me = createMeQuery();
  const queryClient = useQueryClient();
  const change = createChangePassword();
  const hasPassword = $derived(me.data?.user.hasPassword ?? true);
  const form = createForm(
    () =>
      z
        .object({
          current: hasPassword ? PasswordInputSchema : z.string().optional(),
          password: NewPasswordSchema,
          repeat: z.string(),
        })
        .refine((v) => v.password === normalizePassword(v.repeat), { path: ['repeat'], message: fa.password.mismatch }),
    { current: '', password: '', repeat: '' },
  );

  const submit = form.submit(({ current, password }) => {
    change.mutate(
      { ...(hasPassword && current ? { currentPassword: current } : {}), newPassword: password },
      {
        onSuccess: () => {
          form.reset();
          void queryClient.invalidateQueries({ queryKey: meQueryKey });
        },
      },
    );
  });
</script>

<form onsubmit={submit} novalidate class="flex flex-col gap-4">
  {#if !hasPassword}<Alert tone="info">{fa.password.firstTime}</Alert>{/if}
  {#if hasPassword}
    <Field label={fa.password.current} error={form.errors.current}>
      {#snippet children(a11y)}
        <PasswordInput
          {...a11y}
          name="current"
          bind:value={form.values.current}
          oninput={() => form.touch('current')}
          autocomplete="current-password"
          autofocus
          showLabel={fa.auth.showPassword}
          hideLabel={fa.auth.hidePassword}
        />
      {/snippet}
    </Field>
  {/if}
  <Field label={fa.password.newPassword} hint={fa.password.hint} error={form.errors.password}>
    {#snippet children(a11y)}
      <PasswordInput
        {...a11y}
        name="password"
        bind:value={form.values.password}
        oninput={() => form.touch('password')}
        autocomplete="new-password"
        showLabel={fa.auth.showPassword}
        hideLabel={fa.auth.hidePassword}
      />
    {/snippet}
  </Field>
  <Field label={fa.password.repeat} error={form.errors.repeat}>
    {#snippet children(a11y)}
      <PasswordInput
        {...a11y}
        name="repeat"
        bind:value={form.values.repeat}
        oninput={() => form.touch('repeat')}
        autocomplete="new-password"
        showLabel={fa.auth.showPassword}
        hideLabel={fa.auth.hidePassword}
      />
    {/snippet}
  </Field>
  {#if change.error}<ErrorAlert error={change.error} />{/if}
  {#if change.isSuccess}<Alert tone="success">{fa.password.changed}</Alert>{/if}
  <Button type="submit" loading={change.isPending} done={change.isSuccess} disabled={!online.value} class="self-start">{fa.password.changeSubmit}</Button>
</form>
